import "@xyflow/react/dist/base.css";
import "@/modules/workflow/styles/reactflow.css";

import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ActionButton, ActionButtonSize, Card, type CardProps, type GridCard, LoadingSpinner } from "widgets";
import { useVisuallyLoading } from "widgets/hooks";

import type { ActivityInstance } from "@/graphql.ts";
import { WorkflowPreview } from "@/modules/workflow/components/WorkflowPreview.tsx";
import {
  useGetWorkflowEventPreviewQuery,
  useLoadWorkflowPreviewPipelineQuery,
} from "@/modules/workflow/graphql/workflow.gql.ts";
import { type WorkflowActivityWithStatus } from "@/modules/workflow/utils/generateWorkflow.ts";
import type { Activity } from "@/modules/workflow/utils/types.ts";

export interface WorkflowPreviewCardProps extends GridCard, CardProps {
  workflowId?: number | null;
  activities?: Activity[];
  eventId?: number;
}

export const WorkflowPreviewCard: FC<WorkflowPreviewCardProps> = ({ workflowId, activities, eventId, ...props }) => {
  const { data, loading, error } = useLoadWorkflowPreviewPipelineQuery({
    variables: { workflowId },
    skip: !workflowId,
  });

  if (error) {
    throw error;
  }

  const isVisuallyLoading = useVisuallyLoading(loading || !!props.updating, 0, 300);
  const workflow = data?.workflow.findWorkflowById;
  const nodes = workflow?.config.activities;
  const edges = workflow?.config.connectors;

  return (
    <Card title="Preview" contentStyle={{ height: "400px" }} {...props} data-testid="workflow-preview-card">
      {isVisuallyLoading && <LoadingSpinner inCenter />}
      {nodes && edges && activities && (
        <WorkflowPreview
          workflowActivities={nodes as ActivityInstance[]}
          workflowConnectors={edges}
          processorActivities={activities}
          eventId={eventId}
        />
      )}
    </Card>
  );
};

export interface WorkflowEventPreviewCardProps extends GridCard, CardProps {
  workflowEventId?: number;
  activities?: Activity[];
  hideActions?: boolean;
}

export const WorkflowEventPreviewCard: FC<WorkflowEventPreviewCardProps> = ({
  workflowEventId,
  activities,
  ...props
}) => {
  const { data, loading, error } = useGetWorkflowEventPreviewQuery({
    variables: { workflowEventId: workflowEventId! },
    skip: !workflowEventId,
  });

  if (error) {
    throw error;
  }

  const isVisuallyLoading = useVisuallyLoading(loading || props.isLoading || !!props.updating, 0, 300);
  const workflow = data?.workflow.findWorkflowEventById;
  const edges = workflow?.config.connectors;
  const [nodes, setNodes] = useState<WorkflowActivityWithStatus[]>([]);

  useEffect(() => {
    if (workflow?.config.activities) setNodes(workflow?.config.activities as WorkflowActivityWithStatus[]);
  }, [workflow]);

  const navigate = useNavigate();

  // Need to wait for the nodes to be there before updating them

  return (
    <Card
      title={workflow?.alias ?? " "}
      contentStyle={{ height: "400px" }}
      actions={
        props.hideActions
          ? undefined
          : [
              <ActionButton
                key="open"
                onPress={() => (workflow ? navigate(`/processor/session/${workflow.workflowEventId}`) : undefined)}
                label="Open Session"
                size={ActionButtonSize.Small}
                data-testid="open-session"
              />,
            ]
      }
      data-testid="workflow-session-preview-card"
    >
      {isVisuallyLoading && <LoadingSpinner inCenter />}
      {nodes && edges && activities && !isVisuallyLoading && (
        <WorkflowPreview
          workflowActivities={nodes as ActivityInstance[]}
          workflowConnectors={edges}
          processorActivities={activities}
          eventId={workflowEventId}
        />
      )}
    </Card>
  );
};
